import { AnimatePresence, motion } from 'framer-motion'
import { FC, useEffect, useState } from 'react'
import qrCode from 'qrcode'
import { MdCheckCircle, MdQrCode2 } from 'react-icons/md'
import { enqueueSnackbar } from 'notistack'
import { webApiService } from '@/services'
import { Button } from '@/components/ui/button'
import { badgeStyle, REUSE_COLORS } from '@/utils/reuseBrand'
import phonecheckLogo from '@/assets/images/phonecheck-logo.avif'
import StepCard from './StepCard'
import { useStepFooterAction } from '../context/StepFooterActionContext'
import { useTradeinContext } from '../hooks/useTradeinContext'

const PhonecheckCard: FC = () => {
  const { ctx, saveCtx } = useTradeinContext()
  const [urlQrCode, setUrlQrCode] = useState<string | null>(null)
  const [isChecking, setIsChecking] = useState(false)

  useEffect(() => {
    setUrlQrCode(null)
    qrCode.toDataURL(`https://cloudportal.phonecheck.com/cloud/remoteApp/configuration?pin=${ctx.tradein.login_code}`, { errorCorrectionLevel: 'H' }, (err, url) => {
      if (!err) setUrlQrCode(url)
    })
  }, [ctx.tradein.login_code])

  const checkLoginCode = async () => {
    setIsChecking(true)
    try {
      const res = await webApiService.checkLoginCode({ idTradein: ctx.tradein.id, loginCode: ctx.tradein.login_code })
      setIsChecking(false)
      if (res.success) {
        enqueueSnackbar(res.message, { variant: 'success' })
        saveCtx({ ...ctx, reload: true })
      } else {
        enqueueSnackbar(res.message, { variant: 'info' })
      }
    } catch (error) {
      enqueueSnackbar('Error al verificar el código. Por favor, vuelva a intentar.', { variant: 'error' })
      setIsChecking(false)
    }
  }

  const inlineAction = useStepFooterAction(
    (
      <Button onClick={checkLoginCode} disabled={isChecking}>
        <MdCheckCircle className='text-base' />
        {isChecking ? 'Verificando...' : 'Verificar Código'}
      </Button>
    ),
    [isChecking, ctx.tradein.login_code]
  )

  return (
    <StepCard icon={<MdQrCode2 />} title='Realizar prueba' subtitle='Verificación con Phonecheck'>
      <p className='text-sm text-slate-500 mb-3'>El equipo debe correr el test desde la aplicación Phonecheck antes de verificar el código.</p>
      <motion.div
        initial={{ scale: 0.97, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.2, delay: 0.05 }}
        className='rounded-xl p-3 text-center mb-3'
        style={badgeStyle(REUSE_COLORS.risingLilac, 0.25)}
      >
        <p className='text-tiny font-semibold text-slate-500'>Código de evaluación</p>
        <p className='font-black text-3xl tracking-[0.3em] mt-1' style={{ color: REUSE_COLORS.solidPurple }}>{ctx.tradein.login_code}</p>
      </motion.div>
      <div className='flex flex-col items-center gap-2 px-4 py-3'>
        <div className='flex items-center gap-2 text-slate-500'>
          <MdQrCode2 className='text-xl shrink-0' />
          <span className='text-sm font-medium'>Ingresa el código o escanea el QR</span>
          <img src={phonecheckLogo} alt='Phonecheck' className='h-4 w-auto object-contain' />
        </div>
        <AnimatePresence mode='wait'>
          {urlQrCode !== null
            ? (
              <motion.img
                key='qr'
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.25 }}
                src={urlQrCode}
                alt='Código QR de la prueba'
                width={140}
                height={140}
                className='rounded-lg'
              />
              )
            : (
              <motion.div
                key='loading'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className='flex h-[140px] w-[140px] items-center justify-center rounded-lg bg-slate-50'
              >
                <motion.span
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  className='h-6 w-6 rounded-full border-2 border-slate-200 border-t-[var(--tw-color)]'
                  style={{ borderTopColor: REUSE_COLORS.reBlue }}
                />
              </motion.div>
              )}
        </AnimatePresence>
      </div>
      {/* Sin footer compartido (página de detalle clásica) el botón del paso se dibuja acá. */}
      {inlineAction && <div className='flex justify-end pt-4'>{inlineAction}</div>}
    </StepCard>
  )
}

export default PhonecheckCard
